import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import usePrefersReducedMotion from "./usePrefersReducedMotion";

// Only for when `transitionend` never arrives (the element is hidden mid-morph); longer than
// --dur-layout (320ms), which every width transition in the kit runs on.
const WIDTH_MORPH_SETTLE_FALLBACK_MS: number = 600;

/**
 * One width morph at a time on one element: the element is pinned at the width it had, released to
 * the width it wants, and handed back to its content when the transition ends.
 *
 * The transition itself is the element's own CSS (`width var(--dur-layout) …`); this only sets the
 * two ends. Widths are layout widths (`offsetWidth`), never `getBoundingClientRect()`: a control
 * that morphs on the click ending a press is still springing back from its press scale, and a
 * transformed box would pin it a few pixels short of its real size.
 *
 * Both measurements stay with the caller, because the two callers take them at different moments:
 * a class component can read the width *before* React mutates the DOM (`getSnapshotBeforeUpdate`),
 * a hook can only read it after, from what it remembered.
 */
export class WidthMorph {
  private element: HTMLElement | null = null;
  private settleTimer: number | null = null;

  /**
   * Pin `element` at `fromWidth`, then release it to `toWidth`. Any morph already running on this
   * instance stops listening first, so the element is left where it is drawn and carries on from
   * there. The caller has already checked that the two widths differ and that motion is wanted.
   */
  start(element: HTMLElement, fromWidth: number, toWidth: number): void {
    this.stopListening();

    // `transition: none` for the pin: after React has written the new content the element already
    // computes at its new width, so pinning it without this would animate backwards.
    const inlineTransition: string = element.style.transition;
    element.style.transition = "none";
    element.style.width = `${fromWidth}px`;
    // Commit the start width before releasing it, or the browser would never see a change.
    void element.offsetWidth;
    element.style.transition = inlineTransition;
    element.style.width = `${toWidth}px`;

    this.element = element;
    element.addEventListener("transitionend", this.handleTransitionEnd);
    this.settleTimer = window.setTimeout(this.settle, WIDTH_MORPH_SETTLE_FALLBACK_MS);
  }

  /**
   * Drop the inline width, so the element is sized by its content again, and stop listening. Safe
   * at any time; an element this instance never pinned is left alone.
   */
  settle = (): void => {
    const element: HTMLElement | null = this.element;
    this.stopListening();

    if (element !== null) {
      element.style.width = "";
    }
  };

  private stopListening(): void {
    if (this.settleTimer !== null) {
      window.clearTimeout(this.settleTimer);
      this.settleTimer = null;
    }

    if (this.element !== null) {
      this.element.removeEventListener("transitionend", this.handleTransitionEnd);
      this.element = null;
    }
  }

  private handleTransitionEnd = (event: TransitionEvent): void => {
    // Transitions inside the content bubble here too; only the element's own width ends the morph.
    if (event.target === this.element && event.propertyName === "width") {
      this.settle();
    }
  };
}

/**
 * Width morph for an element whose content changes: when `contentKey` changes, the element's width
 * moves from the width it settled at to the width the new content wants. One measurement per
 * change, no ResizeObserver. Off when `isEnabled` is false and under reduced motion, where the
 * width simply follows the content.
 *
 * The element must not hold its old content in flow while it leaves, or the new width would be
 * measured as the wider of the two — a `KuikaSwap` inside it needs `leaving="overlay"`.
 */
export const useWidthMorph = (
  ref: React.RefObject<HTMLElement>,
  contentKey: string | undefined,
  isEnabled: boolean
): void => {
  // Lazily, so the morph is allocated once rather than on every render.
  const [morph] = useState<WidthMorph>((): WidthMorph => new WidthMorph());
  const settledWidthRef: React.MutableRefObject<number | null> = useRef<number | null>(null);
  const prefersReducedMotion: boolean = usePrefersReducedMotion();

  useLayoutEffect((): void => {
    const element: HTMLElement | null = ref.current;

    if (!isEnabled || element === null) {
      settledWidthRef.current = null;
      // Turned off mid-morph: let go of the frozen width.
      morph.settle();
      return;
    }

    // Mid-morph (an inline width is still set) the element starts from where it is drawn now;
    // settled, the DOM already holds the new content, so it starts from the width it settled at.
    const isMorphing: boolean = element.style.width !== "";
    const fromWidth: number | null = isMorphing ? element.offsetWidth : settledWidthRef.current;

    morph.settle();
    const toWidth: number = element.offsetWidth;
    settledWidthRef.current = toWidth;

    if (prefersReducedMotion || fromWidth === null || fromWidth === toWidth) {
      return;
    }

    morph.start(element, fromWidth, toWidth);
  }, [morph, ref, contentKey, isEnabled, prefersReducedMotion]);

  useEffect((): (() => void) => (): void => morph.settle(), [morph]);
};
